/**
 * useSettings Hook
 * Manages user settings, preferences, and credentials
 *
 * Updated: January 9, 2026
 * Now uses API instead of SQLite for DynamoDB storage
 */

import { useState, useEffect, useCallback } from 'react'
import { getAPIBaseUrl } from '../environment'

// Get API base URL
const API_BASE_URL = getAPIBaseUrl()

// Helper to get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('modernizeit_token')
}

// Helper to make authenticated API calls
const fetchWithAuth = async (url, options = {}) => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found')
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  })

  if (!response.ok) {
    if (response.status === 401) {
      // Token expired or invalid - clear token and dispatch logout event
      console.warn(' [Auth] Token expired or invalid - clearing token and logging out')
      localStorage.removeItem('modernizeit_token')
      localStorage.removeItem('modernizeit_user')

      // Dispatch logout event so other components can react
      window.dispatchEvent(new CustomEvent('auth-logout', {
        detail: { reason: 'token_expired' }
      }))

      // Throw error to stop retry loop
      const error = new Error('Session expired - please login again')
      error.isAuthError = true  // Flag so we can detect auth errors
      throw error
    }
    throw new Error(`API error: ${response.statusText}`)
  }

  return response.json()
}

export function useSettings() {

  // User profile
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
  })

  // Preferences
  const [preferences, setPreferences] = useState({
    theme: 'scoutitai-portal',
    fontSize: 14,
    editorSettings: {
      tabSize: 4,
      wordWrap: true,
      minimap: true,
    },
  })

  // Notifications
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    slackNotifications: false,
    jobCompletionAlerts: true,
    errorAlerts: true,
  })

  // AWS Credentials — POC: only safe metadata, never secret values
  const [awsCredentials, setAWSCredentials] = useState({
    configured:        false,
    region:            'us-east-1',
    accessKeyIdMasked: null,
    managed:           true,
  })

  // AI Providers
  const [aiProviders, setAIProviders] = useState({
    claude: { apiKey: '', model: 'claude-sonnet-4-20250514', enabled: false },
    openai: { apiKey: '', model: 'gpt-4o', enabled: false },
    bedrock: { enabled: false, region: 'us-east-1' },
  })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Load all settings from API
  const loadSettings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Check if user is logged in
      const token = getAuthToken()
      if (!token) {
        console.log('[Settings] No auth token found - skipping settings load')
        setLoading(false)
        return
      }

      // FIRST: Load user info from localStorage (set during login)
      const userDataStr = localStorage.getItem('modernizeit_user')
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr)
          console.log('[Settings] Loading user from localStorage:', userData)
          setProfile({
            firstName: userData.name ? userData.name.split(' ')[0] : '',
            lastName: userData.name ? userData.name.split(' ').slice(1).join(' ') : '',
            displayName: userData.name || '',
            name: userData.name || '',
            email: userData.email || '',
            language: 'en',
            dateFormat: 'MM/DD/YYYY',
          })
        } catch (err) {
          console.error('[Settings] Failed to parse user from localStorage:', err)
        }
      }

      // THEN: Fetch settings from API (may override with saved preferences)
      // Token already fetched above (line 101), reuse it
      console.log(` [Settings] Calling /auth/settings - Token present: ${!!token}, Token length: ${token?.length || 0}`)
      const settings = await fetchWithAuth(`${API_BASE_URL}/auth/settings`)

      // Load profile preferences (merge with localStorage data)
      if (settings.profile) {
        setProfile(prev => ({
          ...prev,
          firstName: settings.profile.firstName || prev.firstName,
          lastName: settings.profile.lastName || prev.lastName,
          displayName: settings.profile.displayName || prev.displayName,
          name: settings.profile.displayName || settings.profile.name || prev.name,
          email: settings.profile.email || prev.email,
          language: settings.profile.language || prev.language || 'en',
          dateFormat: settings.profile.dateFormat || prev.dateFormat || 'MM/DD/YYYY',
        }))
      }

      // Load preferences
      const theme = settings.preferences?.theme || 'scoutitai-portal'
      setPreferences({
        theme: theme,
        fontSize: settings.preferences?.fontSize || 14,
        editorSettings: settings.preferences?.editorSettings || {
          tabSize: 4,
          wordWrap: true,
          minimap: true,
        },
      })

      // Apply theme to document
      console.log('[useSettings] Applying loaded theme:', theme)
      document.documentElement.setAttribute('data-theme', theme)

      // Load notifications
      if (settings.notifications) {
        setNotifications({
          emailNotifications: settings.notifications.emailNotifications ?? true,
          slackNotifications: settings.notifications.slackNotifications ?? false,
          jobCompletionAlerts: settings.notifications.jobCompletionAlerts ?? true,
          errorAlerts: settings.notifications.errorAlerts ?? true,
        })
      }

      // Load AWS credentials — POC: store only safe metadata, never secrets
      if (settings.awsCredentials) {
        setAWSCredentials({
          configured:        settings.awsCredentials.configured ?? false,
          region:            settings.awsCredentials.region || 'us-east-1',
          accessKeyIdMasked: settings.awsCredentials.accessKeyIdMasked || null,
          managed:           settings.awsCredentials.managed ?? true,
        })
      }

      // Load AI providers
      if (settings.aiProviders) {
        setAIProviders({
          claude: settings.aiProviders.claude || { apiKey: '', model: 'claude-sonnet-4-20250514', enabled: false },
          openai: settings.aiProviders.openai || { apiKey: '', model: 'gpt-4o', enabled: false },
          bedrock: settings.aiProviders.bedrock || { enabled: false, region: 'us-east-1' },
        })
      }

    } catch (err) {
      // Check if this is an auth error (token expired)
      if (err.isAuthError) {
        console.warn(' [Settings] Auth error - stopping retry loop')
        setError('Session expired - please login again')
        // Don't retry on auth errors
      } else {
        setError(err.message)
        console.error('Failed to load settings:', err)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  // Load settings on mount
  useEffect(() => {
    loadSettings()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for settings changes from other components
  useEffect(() => {
    const handleSettingsChanged = (event) => {
      if (event.detail?.preferences) {
        setPreferences(prev => ({ ...prev, ...event.detail.preferences }))
        // Apply theme if it changed
        if (event.detail.preferences.theme) {
          console.log('[useSettings] Settings changed, applying theme:', event.detail.preferences.theme)
          document.documentElement.setAttribute('data-theme', event.detail.preferences.theme)
        }
      }
    }
    window.addEventListener('settings-changed', handleSettingsChanged)
    return () => window.removeEventListener('settings-changed', handleSettingsChanged)
  }, [])

  // Update profile
  const updateProfile = useCallback(async (data) => {
    setSaving(true)
    setError(null)
    try {
      const newProfile = { ...profile, ...data }
      setProfile(newProfile)

      // Save to API
      await fetchWithAuth(`${API_BASE_URL}/auth/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          profile: {
            firstName: data.firstName || newProfile.firstName,
            lastName: data.lastName || newProfile.lastName,
            displayName: data.name || data.displayName || newProfile.name,
            email: data.email || newProfile.email,
            language: data.language || newProfile.language,
            dateFormat: data.dateFormat || newProfile.dateFormat,
          }
        })
      })

      return newProfile
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setSaving(false)
    }
  }, [profile])

  // Update preferences
  const updatePreferences = useCallback(async (data) => {
    setSaving(true)
    setError(null)
    try {
      const newPreferences = { ...preferences, ...data }
      console.log('[useSettings] Updating preferences:', newPreferences)
      setPreferences(newPreferences)

      // Save to API
      const result = await fetchWithAuth(`${API_BASE_URL}/auth/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          preferences: newPreferences
        })
      })

      console.log('[useSettings] API save result:', result)

      // Dispatch event so other components can react to settings change
      window.dispatchEvent(new CustomEvent('settings-changed', { detail: { preferences: newPreferences } }))
      return newPreferences
    } catch (err) {
      console.error('[useSettings] Failed to update preferences:', err)
      setError(err.message)
      throw err
    } finally {
      setSaving(false)
    }
  }, [preferences])

  // Update theme
  const setTheme = useCallback(async (theme) => {
    return updatePreferences({ theme })
  }, [updatePreferences])

  // Update font size
  const setFontSize = useCallback(async (fontSize) => {
    return updatePreferences({ fontSize })
  }, [updatePreferences])

  // Update editor settings
  const updateEditorSettings = useCallback(async (settings) => {
    return updatePreferences({
      editorSettings: { ...preferences.editorSettings, ...settings }
    })
  }, [updatePreferences, preferences.editorSettings])

  // Update notifications
  const updateNotifications = useCallback(async (data) => {
    setSaving(true)
    setError(null)
    try {
      const newNotifications = { ...notifications, ...data }
      setNotifications(newNotifications)

      // Save to API
      await fetchWithAuth(`${API_BASE_URL}/auth/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          notifications: newNotifications
        })
      })

      return newNotifications
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setSaving(false)
    }
  }, [notifications])

  // Update AWS credentials
  const updateAWSCredentials = useCallback(async (data) => {
    setSaving(true)
    setError(null)
    try {
      const newCreds = { ...awsCredentials, ...data }
      setAWSCredentials(newCreds)

      // Save to API (will be encrypted)
      await fetchWithAuth(`${API_BASE_URL}/auth/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          awsCredentials: newCreds
        })
      })

      return newCreds
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setSaving(false)
    }
  }, [awsCredentials])

  // Update AI provider
  const updateAIProvider = useCallback(async (provider, data) => {
    setSaving(true)
    setError(null)
    try {
      const newConfig = { ...aiProviders[provider], ...data }
      const newAIProviders = {
        ...aiProviders,
        [provider]: newConfig,
      }
      setAIProviders(newAIProviders)

      // Save to API (will be encrypted)
      await fetchWithAuth(`${API_BASE_URL}/auth/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          aiProviders: newAIProviders
        })
      })

      return newConfig
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setSaving(false)
    }
  }, [aiProviders])

  // Test AWS connection
  const testAWSConnection = useCallback(async () => {
    // Would call API to test connection
    console.log('Testing AWS connection...')
    return { success: true, message: 'Connection successful' }
  }, [])

  // Test AI provider connection
  const testAIConnection = useCallback(async (provider) => {
    // Would call API to test connection
    console.log(`Testing ${provider} connection...`)
    return { success: true, message: 'Connection successful' }
  }, [])

  return {
    // State
    profile,
    preferences,
    notifications,
    awsCredentials,
    aiProviders,
    theme: preferences.theme,
    fontSize: preferences.fontSize,
    loading,
    saving,
    error,

    // Actions
    loadSettings,
    updateProfile,
    updatePreferences,
    setTheme,
    setFontSize,
    updateEditorSettings,
    updateNotifications,
    updateAWSCredentials,
    updateAIProvider,
    testAWSConnection,
    testAIConnection,
  }
}

export default useSettings
